import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject, Subscription, debounceTime, distinctUntilChanged, from, map, of, switchMap } from 'rxjs';

import { AddressAutocompleteService, AddressSuggestion, PlacesSession } from './address-autocomplete.service';
import { SuggestedAddress } from './address-from-place';
import { searchCountries } from './places-countries';

/** How long typing has to pause before Google is asked. */
export const SEARCH_DEBOUNCE_MS = 300;
/** Fewer characters than this say too little to search on. */
export const MIN_SEARCH_LENGTH = 3;

/**
 * Address suggestions from Google for a street field. The field stays a plain
 * input that can be typed in freely; picking a suggestion hands its address to
 * the form, which fills its own fields. Whether the address is deliverable is
 * still Easyship's to say.
 *
 * Wired to the street input by the form:
 *
 *   <input ... [matAutocomplete]="street.panel" [matAutocompleteDisabled]="!street.active"
 *          (focus)="street.warmUp()" (input)="street.search($any($event.target).value)" />
 *   <app-address-suggestions #street [country]="..." (picked)="..." />
 *
 * Only typing searches: `(input)` fires for what the user types, never for a
 * value set by code — loading a record, applying a pick, or a corrected address.
 */
@Component({
  selector: 'app-address-suggestions',
  standalone: true,
  imports: [CommonModule, MatAutocompleteModule],
  template: `
    <mat-autocomplete #panel="matAutocomplete" class="address-suggestions-panel">
      <mat-option
        *ngFor="let suggestion of suggestions"
        [value]="suggestion.main"
        (onSelectionChange)="$event.isUserInput && $event.source.selected && choose(suggestion)"
      >
        <span class="address-suggestion__main">{{ suggestion.main }}</span>
        <span class="address-suggestion__secondary" *ngIf="suggestion.secondary">{{ suggestion.secondary }}</span>
      </mat-option>
      <!-- Google's terms ask for its name wherever its suggestions are shown without a map. -->
      <mat-option *ngIf="suggestions.length" disabled class="address-suggestion__attribution">Google Maps</mat-option>
    </mat-autocomplete>
  `,
  styles: [
    `
      /* The list opens in an overlay; this element itself takes no room in the form's layout. */
      :host {
        display: none;
      }
      .address-suggestion__main {
        display: block;
        line-height: 1.3;
      }
      .address-suggestion__secondary {
        display: block;
        font-size: 12px;
        line-height: 1.3;
        color: rgba(0, 0, 0, 0.6);
      }
      .address-suggestion__attribution {
        min-height: 28px;
        font-size: 11px;
        justify-content: flex-end;
      }
    `,
  ],
})
export class AddressSuggestionsComponent implements OnInit, OnDestroy {
  @ViewChild('panel', { static: true }) panel!: MatAutocomplete;

  /** The form's country: suggestions come from it alone when it is one of the configured countries. */
  @Input() country: string | null | undefined = null;
  /** A form that takes US addresses only, whatever its country field says. */
  @Input() usOnly = false;
  /** The address of the suggestion picked. */
  @Output() picked = new EventEmitter<SuggestedAddress>();

  suggestions: AddressSuggestion[] = [];

  private readonly places = inject(AddressAutocompleteService);
  private readonly zone = inject(NgZone);
  private readonly typed = new Subject<string>();
  private session: PlacesSession | null = null;
  private searching?: Subscription;

  ngOnInit(): void {
    this.searching = this.typed
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        map((text) => text.trim()),
        distinctUntilChanged(),
        // A newer search replaces one still on its way, whose answer is then dropped.
        switchMap((text) =>
          text.length < MIN_SEARCH_LENGTH || !this.active
            ? of(null)
            : from(this.places.suggest(text, this.regions, this.session)),
        ),
      )
      .subscribe((answer) =>
        this.zone.run(() => {
          this.suggestions = answer?.suggestions ?? [];
          if (answer) {
            this.session = answer.session;
          }
        }),
      );
  }

  ngOnDestroy(): void {
    this.searching?.unsubscribe();
  }

  /** The countries this field's searches cover. */
  get regions(): string[] {
    return searchCountries(this.places.countries, this.usOnly ? 'US' : this.country);
  }

  /** Whether the field offers suggestions; otherwise it is a plain input. */
  get active(): boolean {
    return this.places.available && this.regions.length > 0;
  }

  /** The field got focus: load Google's script now, so the first search does not wait for it. */
  warmUp(): void {
    if (this.active) {
      this.places.warmUp();
    }
  }

  /** What the user typed in the street field. */
  search(text: string | null | undefined): void {
    this.typed.next(text ?? '');
  }

  /** A suggestion picked: its address closes this search's session, and the next keystroke opens another. */
  async choose(suggestion: AddressSuggestion): Promise<void> {
    this.session = null;
    this.suggestions = [];
    // Forget the last search, so the same text typed again is searched again.
    this.typed.next('');
    const address = await this.places.addressOf(suggestion);
    if (address) {
      this.zone.run(() => this.picked.emit(address));
    }
  }
}
